import { resolveModal } from "./modalService";
import LoadingSpinner from "../loadingSpinner/loadingSpinner";
import MainModal from "./examples/mainModal";
import ConfirmModal from "./examples/confirmModal";
import ErrorModal from "./examples/errorModal";
import ConfirmationModal from "../modals/confirmation/index";

import { useDispatch, useSelector } from "react-redux";
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { closeModal } from "modules/common/store/modalsSlice";

import DraggableModal from "./draggableModal";
import "./index.scss";

const MODAL_COMPONENTS = {
    CONFIRM_MODAL: ConfirmModal,
    LOADING_MODAL: LoadingSpinner,
    MAIN_MODAL: MainModal,
    ERROR_MODAL: ErrorModal,
    CONFIRMATION_MODAL: ConfirmationModal
};

export default function ModalContainer() {
    const modals = useSelector(state => state.modals);
    //console.log("Current modals:", modals);
    const dispatch = useDispatch();
    const [fadeIds, setFadeIds] = useState([]);

    const [zIndexes, setZIndexes] = useState({});

    const bringToFront = (id) => {
        setZIndexes(prev => {
            const others = Object.entries(prev)
                .filter(([key]) => key !== id)
                .map(([, value]) => value);

            const max = Math.max(7000, ...others);
            return { ...prev, [id]: max + 1 };
        });
    };

    useEffect(() => {
        document.body.style.overflow = modals.active.length > 0 ? "hidden" : "";
    }, [modals.active]);

    const handleClose = (id, result = null) => {
        setFadeIds(prev => [...prev, id]);

        setTimeout(() => {
            resolveModal(id, result); // resolve promise
            dispatch(closeModal(id));
            setFadeIds(prev => prev.filter(fid => fid !== id));
        }, 300);
    };

    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === "Escape" && modals.active.length > 0) {
                const top = modals.active[modals.active.length - 1];
                handleClose(top.id, false);
            }
        };
        document.addEventListener("keydown", handleEsc);
        return () => document.removeEventListener("keydown", handleEsc);
    }, [modals.active]);

    useEffect(() => {
        modals.active.forEach(modal => {
            if (modal.options?.autoCloseAfter) {
                const timer = setTimeout(() => {
                    handleClose(modal.id);
                }, modal.options.autoCloseAfter);

                return () => clearTimeout(timer);
            }
        });
    }, [modals.active]);

    useEffect(() => {
        const focusable = document.querySelectorAll(
            ".modal-wrapper button, .modal-wrapper input, .modal-wrapper [tabindex]"
        );

        if (focusable.length) focusable[0].focus();

        const trap = (e) => {
            if (e.key !== "Tab") return;

            const first = focusable[0];
            const last = focusable[focusable.length - 1];

            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        };

        document.addEventListener("keydown", trap);
        return () => document.removeEventListener("keydown", trap);
    }, [modals.active]);

    return createPortal(
        <>
            {modals?.active?.map((modal, index) => {
                const ModalComponent = MODAL_COMPONENTS[modal.modalType];
                const baseZ = 7000 + index * 10;
                const zIndex = zIndexes[modal.id] || baseZ;

                //console.log(`Rendering modal ${modal}, with z-index ${zIndex}`);

                const isFading = fadeIds.includes(modal.id);

                return (
                    <React.Fragment key={modal.id}>
                        <div
                            className={`modal-overlay ${isFading ? "fade-out" : "fade-in"}`}
                            style={{ zIndex }}
                            onClick={
                                modal.options?.closeOnOutsideClick !== false
                                    ? () => handleClose(modal.id, false)
                                    : undefined
                            }
                        />

                        <DraggableModal
                            modal={modal}
                            stackIndex={index}
                            isFading={isFading}
                            baseZ={zIndex + 1}
                            bringToFront={bringToFront}
                            handleClose={handleClose}
                            ModalComponent={ModalComponent}
                        />
                    </React.Fragment>
                );
            })}
        </>,
        document.body
    );
}